import moonArt from '../../../../assets/moon.PNG';
import statueArt from '../../../../assets/statue.PNG';

export type ArtName = 'moon' | 'statue';

const SRC: Record<ArtName, string> = { moon: moonArt, statue: statueArt };

/**
 * Halo behaviour:
 *  - `breathe` — first entry: strong baseline that rises and returns, never zero
 *  - `flicker` — Home hero: luminous baseline with an occasional brighter catch
 *  - `steady`  — small identity mark: constant, no pulse at all
 */
export type HaloMode = 'breathe' | 'flicker' | 'steady';

export interface AstralArtProps {
  name: ArtName;
  halo: HaloMode;
  /** Adds the activation state to the halo (first entry only). */
  activated?: boolean;
  className?: string;
  alt?: string;
}

/**
 * The finished Columbina artwork, presented untouched.
 *
 * A second copy of the SAME asset sits exactly behind the real one carrying a
 * layered `drop-shadow`. Because drop-shadow follows the PNG's alpha, the glow
 * traces the true silhouette — crescent, figure and pedestal — instead of being
 * a blurred ellipse. The copy's own pixels are entirely covered by the real
 * image above it, so only the light spilling past the outline is visible.
 *
 * The displayed image therefore carries NO filter, mask, tint or crop. Only
 * `opacity` is ever animated, so the (expensive) blur is rasterised once and
 * never recomputed per frame.
 *
 * Sizing is driven by `aspect-ratio` + `object-fit: contain`, so the box always
 * matches the asset's natural proportions and the artwork can never be cropped
 * or distorted at any viewport.
 */
export function AstralArt({ name, halo, activated = false, className, alt = '' }: AstralArtProps): JSX.Element {
  const src = SRC[name];
  return (
    <span className={`art art--${name}${className ? ` ${className}` : ''}`}>
      <img
        className={`art__halo art__halo--${halo}${activated ? ' is-activated' : ''}`}
        src={src}
        alt=""
        aria-hidden="true"
        decoding="async"
      />
      <img className="art__img" src={src} alt={alt} decoding="async" />
    </span>
  );
}
